// src/components/partners/DistributorForm.jsx
import React, { useState, useEffect } from 'react';
import { Form, Card, Alert, Row, Col, InputGroup, Button } from 'react-bootstrap';
import { Save, X, User, Mail, Phone, MapPin, FileText, Hash } from 'lucide-react';
import useForm from '../../hooks/useForm';
import { createDistributor, getDistributorById, updateDistributor } from '../../api/partner';

const DistributorForm = ({ distributorId, onCancel, onSuccess }) => {
    const isEditMode = !!distributorId;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const initialValues = {
        name: '',
        email: '',
        phone: '',
        address: '',
        vatApplicable: false,
        creditLimit: '',
        contractNumber: ''
    };

    const { values, setValues, handleChange, errors, validateForm } = useForm(initialValues);

    useEffect(() => {
        if (isEditMode) {
            fetchDistributorData();
        }
    }, [distributorId]);

    const fetchDistributorData = async () => {
        try {
            setLoading(true);
            const distributorData = await getDistributorById(distributorId);

            setValues({
                name: distributorData.name || '',
                email: distributorData.email || '',
                phone: distributorData.phone || '',
                address: distributorData.address || '',
                vatApplicable: distributorData.vatApplicable || false,
                creditLimit: distributorData.creditLimit ? distributorData.creditLimit.toString() : '',
                contractNumber: distributorData.contractNumber || ''
            });
        } catch (err) {
            setError('Impossible de charger les données du distributeur. Veuillez réessayer.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const validationSchema = {
        name: {
            required: true,
            errorMessage: 'Le nom du distributeur est obligatoire'
        },
        email: {
            required: true,
            pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            errorMessage: 'Veuillez saisir une adresse email valide'
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        // Valider le formulaire
        const isValid = validateForm(validationSchema);
        if (!isValid) {
            setLoading(false);
            return;
        }

        try {
            const distributorData = {
                ...values,
                creditLimit: values.creditLimit ? parseFloat(values.creditLimit) : null
            };

            if (isEditMode) {
                // Mettre à jour le distributeur existant
                await updateDistributor(distributorId, distributorData);
                setSuccess('Distributeur mis à jour avec succès !');
            } else {
                // Créer un nouveau distributeur
                const result = await createDistributor(distributorData);

                // Si le serveur renvoie un mot de passe temporaire pour le compte utilisateur
                if (result && result.user && result.user.tempPassword) {
                    setSuccess(`Distributeur créé avec succès ! Un compte utilisateur a été créé avec le mot de passe temporaire: ${result.user.tempPassword}`);
                } else {
                    setSuccess('Distributeur créé avec succès !');
                }
            }

            // Appeler le callback de succès après un court délai pour permettre à l'utilisateur de voir le message de succès
            setTimeout(() => {
                if (onSuccess) onSuccess();
            }, 1500);

        } catch (err) {
            setError(err.message || 'Une erreur est survenue. Veuillez réessayer.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setValues(prev => ({ ...prev, [name]: checked }));
    };

    return (
        <Card className="shadow-sm">
            <Card.Header>
                <h5 className="mb-0">{isEditMode ? 'Modifier le distributeur' : 'Ajouter un distributeur'}</h5>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Nom du distributeur</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <User size={18} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={values.name}
                                        onChange={handleChange}
                                        placeholder="Entrez le nom du distributeur"
                                        required
                                    />
                                </InputGroup>
                                {errors.name && <Form.Text className="text-danger">{errors.name}</Form.Text>}
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Email</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <Mail size={18} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={values.email}
                                        onChange={handleChange}
                                        placeholder="Entrez l'email du distributeur"
                                        required
                                    />
                                </InputGroup>
                                {errors.email && <Form.Text className="text-danger">{errors.email}</Form.Text>}
                                {!isEditMode && (
                                    <Form.Text className="text-muted">
                                        Cet email sera utilisé pour créer un compte utilisateur pour le distributeur.
                                    </Form.Text>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Téléphone</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <Phone size={18} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        name="phone"
                                        value={values.phone}
                                        onChange={handleChange}
                                        placeholder="Entrez le numéro de téléphone"
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Numéro de contrat</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <Hash size={18} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        name="contractNumber"
                                        value={values.contractNumber}
                                        onChange={handleChange}
                                        placeholder="Entrez le numéro de contrat"
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label>Adresse</Form.Label>
                        <InputGroup>
                            <InputGroup.Text>
                                <MapPin size={18} />
                            </InputGroup.Text>
                            <Form.Control
                                as="textarea"
                                name="address"
                                value={values.address}
                                onChange={handleChange}
                                placeholder="Entrez l'adresse complète"
                                style={{ height: '80px' }}
                            />
                        </InputGroup>
                    </Form.Group>

                    <Row>
                        <Col md={6}>
                            {/* <Form.Group className="mb-3">
                                <Form.Label>Limite de crédit</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <DollarSign size={18} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        name="creditLimit"
                                        value={values.creditLimit}
                                        onChange={handleChange}
                                        placeholder="Entrez la limite de crédit"
                                    />
                                </InputGroup>
                            </Form.Group> */}
                        </Col>

                        <Col md={6} className="d-flex align-items-center">
                            <Form.Group className="mb-3 mt-2">
                                <Form.Check
                                    type="checkbox"
                                    id="vatApplicable"
                                    name="vatApplicable"
                                    label="Soumis à la TVA"
                                    checked={values.vatApplicable}
                                    onChange={handleCheckboxChange}
                                />
                                <Form.Text className="text-muted">
                                    Cochez si ce distributeur est soumis à la TVA
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>

                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <Button
                            variant="outline-secondary"
                            onClick={onCancel}
                            disabled={loading}
                        >
                            <X size={16} className="me-1" />
                            Annuler
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    {isEditMode ? 'Mise à jour...' : 'Création...'}
                                </>
                            ) : (
                                <>
                                    <Save size={16} className="me-1" />
                                    {isEditMode ? 'Mettre à jour' : 'Créer'}
                                </>
                            )}
                        </Button>
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
};

export default DistributorForm;